import React, { memo } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Box, useColorModeValue } from '@chakra-ui/react';

/**
 * A wrapper component for draggable items in the Kanban board
 * This component handles the drag-and-drop behavior and styling
 */
const NewKBDragWrapper = ({ 
  id, 
  index, 
  children, 
  isDragDisabled = false,
  ...props 
}) => {
  // These hooks must be called at the top level before any conditional returns
  const bgColor = useColorModeValue('white', 'gray.700');
  const dragBgColor = useColorModeValue('gray.100', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const errorBgColor = 'red.100';
  const errorBorderColor = 'red.300';
  
  // For debugging
  console.log(`[NewKBDragWrapper] Rendering with id: ${id}, index: ${index}`);
  
  // Safety check for id
  if (!id || typeof id !== 'string' || !id.startsWith('SUM-')) {
    console.error('[NewKBDragWrapper] Invalid or missing leadId:', { id, index });
    // Render non-draggable fallback
    return (
      <Box
        mb={3}
        p={3}
        bg={errorBgColor}
        borderWidth="1px"
        borderRadius="md"
        borderColor={errorBorderColor}
        boxShadow="sm"
      >
        {children}
      </Box>
    );
  }

  // Always ensure the ID is a string and log it for debugging
  const dragId = String(id);
  
  console.log(`[NewKBDragWrapper] Creating draggable with ID:`, { 
    dragId, 
    index, 
    isDragDisabled 
  });
  
  return (
    <Draggable
      draggableId={dragId}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          mb={3}
          p={3}
          bg={snapshot.isDragging ? dragBgColor : bgColor}
          borderWidth="1px"
          borderRadius="md"
          borderColor={borderColor}
          boxShadow={snapshot.isDragging ? 'md' : 'sm'}
          transition="background 0.2s, box-shadow 0.2s"
          _hover={{
            boxShadow: 'md',
          }}
          {...props}
        >
          {children}
        </Box>
      )}
    </Draggable>
  );
};

// Memoize to prevent unnecessary re-renders
export default memo(NewKBDragWrapper);
